import { useBunja } from 'bunja/react';
import cx from 'classnames';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';

import { useAuth } from '@flipster/features/src/shared/auth/state';
import { Button } from '@flipster/react/design-system/button';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { createSingleChoice } from '@flipster/react/design-system/chip';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import DocumentHistoryIcon from '@flipster/react/icon/generated/DocumentHistoryIcon';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { PositionEvent } from '@flipster/utils/segment/events/position';

import { useClosePositions } from '@web/hooks/trade/use-close-positions';

import { positionSectionHeaderDataBunja } from './state';

type PositionSectionTabType = 'open' | 'pending';
const Choice = createSingleChoice<'open' | 'pending'>();

interface Props {
  tab: PositionSectionTabType;
  onChangeTab: (tab: PositionSectionTabType) => void;
  showAllSymbol: boolean;
  toggleShowAllSymbol: () => void;
}

/** 데이터는 positionSectionHeaderDataBunja(primitive atom)를 consume만 한다 — 값이 실제로 바뀔 때만 리렌더 */
const PositionSectionHeader = ({ tab, onChangeTab, showAllSymbol, toggleShowAllSymbol }: Props) => {
  const t = useTranslations();
  const { isCookieLoggedIn } = useAuth();
  const {
    symbol,
    displayOpenCountAtom,
    displayPendingCountAtom,
    canCloseAllPositionsAtom,
    displayShortNameAtom,
    closablePositionsAtom,
  } = useBunja(positionSectionHeaderDataBunja);
  const displayOpenCount = useAtomValue(displayOpenCountAtom);
  const displayPendingCount = useAtomValue(displayPendingCountAtom);
  const canCloseAllPositions = useAtomValue(canCloseAllPositionsAtom);
  const displayShortName = useAtomValue(displayShortNameAtom);
  const { tryClosePositions, canSkip, isLoading } = useClosePositions();

  // Close all 대상은 매 tick 재계산되는 atom이므로 구독하지 않고 클릭 시점에 스냅샷만 읽는다
  const readClosablePositions = useAtomCallback(
    useCallback((get) => get(closablePositionsAtom), [closablePositionsAtom]),
  );

  return (
    // 헤더 스타일은 positionSection.module.scss(.header/.headerLeft)에서 Panda로 이전 (scss는 섹션 레이아웃 공용이라 파일 이동 대신 전환)
    <header
      className={flex({
        justify: 'space-between',
        align: 'flex-start',
        position: 'sticky',
        zIndex: 3,
        pt: 12,
        pb: 8,
        bgColor: 'background.default.level-0',
      })}
    >
      <div
        className={cx(
          flex({
            justify: 'space-between',
            align: 'center',
            flex: 1,
            wrap: 'wrap',
            gap: 16,
            w: '100%',
            py: 2,
            overflowX: 'auto',
          }),
          tab === 'open' && css({ pr: 16 }),
        )}
      >
        <div className={flex({ gap: 8, align: 'center' })}>
          <Choice value={tab} onChange={onChangeTab}>
            <Choice.Chip value='open' label={t('Assets.r250401.Positions')} badge={displayOpenCount} />
            <Choice.Chip value='pending' label={t('Assets.r251008.Orders')} badge={displayPendingCount} />
          </Choice>
          <IsomorphicLink
            className={css({ padding: 0, color: 'content.default.level-5' })}
            href='/history/perpetual/position'
            newTab
            prefetch={isCookieLoggedIn}
          >
            <DocumentHistoryIcon size={24} />
          </IsomorphicLink>
        </div>
        <div>
          <Checkbox
            checked={showAllSymbol}
            onChange={() => {
              toggleShowAllSymbol();
              segment.track(PositionEvent.YourPositionsShowAllClick, {
                symbol,
                trade_type: 'perpetual',
                order_status: tab === 'open' ? 'Open' : 'Pending',
                is_checked: !showAllSymbol,
              });
            }}
            css={{ textStyle: 'Text/M/Regular', color: 'content.default.level-5' }}
          >
            {t('Trade.r250401.Show_all_positions')}
          </Checkbox>
        </div>
      </div>
      {tab === 'open' && (
        <Button
          size='small'
          color='neutral'
          hierarchy='secondary'
          label={t('Trade.r250401.Close_all_symbol', { symbol: showAllSymbol ? '' : displayShortName })}
          onClick={async () => {
            const closablePositions = readClosablePositions();
            segment.track(PositionEvent.CloseAllClick, {
              symbol,
              num_open_positions: displayOpenCount,
              is_show_all: showAllSymbol,
              is_require_confirm: !canSkip,
            });
            await tryClosePositions({
              positions: closablePositions,
              onAlertConfirmClick: () => {
                segment.track(PositionEvent.CloseAllConfirmClick, {
                  symbol,
                  num_open_positions: displayOpenCount,
                  is_show_all: showAllSymbol,
                  button_text: t('Assets.r250401.Close_all'),
                });
              },
            });
          }}
          disabled={!canCloseAllPositions}
          isLoading={isLoading}
          css={{ mt: 5 }}
          withSpinner
        />
      )}
    </header>
  );
};

export default PositionSectionHeader;
